import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2.js';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle.js';
import Info from 'lucide-react/dist/esm/icons/info.js';
import X from 'lucide-react/dist/esm/icons/x.js';

/**
 * Shared Toast system.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success('Saved!');
 *   toast.error('Could not save', { duration: 6000 });
 *   toast.info('Heads up');
 *
 * Mount <ToastProvider> once near the app root. It renders a fixed-position
 * stack (top-right on desktop, bottom on mobile) of dismissable toasts with
 * auto-dismiss + progress bar.
 */

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;

const VARIANTS = {
  success: {
    role: 'status',
    icon: CheckCircle2,
    container:
      'bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-100',
    bar: 'bg-emerald-500',
    iconColor: 'text-emerald-600 dark:text-emerald-300',
  },
  error: {
    role: 'alert',
    icon: AlertCircle,
    container:
      'bg-red-50 border-red-300 text-red-900 dark:bg-red-900/30 dark:border-red-700 dark:text-red-100',
    bar: 'bg-red-500',
    iconColor: 'text-red-600 dark:text-red-300',
  },
  info: {
    role: 'status',
    icon: Info,
    container:
      'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-100',
    bar: 'bg-blue-500',
    iconColor: 'text-blue-600 dark:text-blue-300',
  },
};

let idCounter = 0;
function nextId() {
  idCounter += 1;
  return `toast-${Date.now()}-${idCounter}`;
}

function ToastItem({ toast, onDismiss }) {
  const variant = VARIANTS[toast.type] || VARIANTS.info;
  const Icon = variant.icon;
  const [entered, setEntered] = useState(false);
  const duration = toast.duration ?? DEFAULT_DURATION;

  useEffect(() => {
    const t = window.setTimeout(() => setEntered(true), 10);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!duration || duration <= 0) return undefined;
    const t = window.setTimeout(() => onDismiss(toast.id), duration);
    return () => window.clearTimeout(t);
  }, [duration, toast.id, onDismiss]);

  return (
    <div
      role={variant.role}
      aria-live={variant.role === 'alert' ? 'assertive' : 'polite'}
      onClick={() => onDismiss(toast.id)}
      className={`pointer-events-auto cursor-pointer relative w-full sm:w-80 max-w-full rounded-lg border shadow-lg overflow-hidden transition-all duration-200 ease-out ${
        variant.container
      } ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
    >
      <div className="flex items-start gap-3 p-3 pr-9">
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${variant.iconColor}`} aria-hidden="true" />
        <div className="text-sm font-medium leading-5 break-words">{toast.message}</div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(toast.id);
          }}
          aria-label="Dismiss"
          className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-current"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      {duration > 0 && (
        <div
          className={`h-1 ${variant.bar} origin-left toast-progress`}
          style={{ animationDuration: `${duration}ms` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((type, message, opts = {}) => {
    const id = opts.id || nextId();
    setToasts((list) => [...list, { id, type, message, duration: opts.duration }]);
    return id;
  }, []);

  const api = useMemo(
    () => ({
      show: (message, opts) => push(opts?.type || 'info', message, opts),
      success: (message, opts) => push('success', message, opts),
      error: (message, opts) => push('error', message, opts),
      info: (message, opts) => push('info', message, opts),
      dismiss,
    }),
    [push, dismiss]
  );

  const value = useMemo(() => ({ toast: api }), [api]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-label="Notifications"
        className="pointer-events-none fixed z-[100] inset-x-0 top-auto bottom-4 px-4 flex flex-col items-center gap-2 sm:inset-auto sm:top-4 sm:right-4 sm:bottom-auto sm:items-end sm:px-0"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
      <style>{`
        @keyframes toast-progress-shrink {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }
        .toast-progress {
          animation-name: toast-progress-shrink;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe fallback so components don't crash if provider is missing.
    if (typeof window !== 'undefined' && !window.__toastProviderWarned) {
      window.__toastProviderWarned = true;
      // eslint-disable-next-line no-console
      console.warn('useToast() called without <ToastProvider>. Toasts will be no-ops.');
    }
    const noop = () => undefined;
    return {
      toast: { show: noop, success: noop, error: noop, info: noop, dismiss: noop },
    };
  }
  return ctx;
}

export default ToastProvider;
